/*
COLORS https://baseweb.design/guides/theming/
DARK MODE https://material.io/design/color/dark-theme.html
*/
import { type ColorTheme, type ColorTokens } from './types';

const colors: ColorTokens = {
  black: 'hsl(0,0%,0%)',
  blue50: 'hsl(224,88.2%,96.7%)',
  blue100: 'hsl(219,87%,91%)',
  blue200: 'hsl(219,86.3%,80%)',
  blue300: 'hsl(219,88.5%,65.9%)',
  blue400: 'hsl(219,87.8%,54.9%)',
  blue500: 'hsl(219,71.8%,41.8%)',
  blue600: 'hsl(219,72.6%,32.9%)',
  blue700: 'hsl(219,71.4%,22%)',
  brown50: 'hsl(30,40%,94.1%)',
  brown100: 'hsl(19,28.6%,89%)',
  brown200: 'hsl(19,27.4%,75.7%)',
  brown300: 'hsl(19,27.1%,58%)',
  brown400: 'hsl(19,33.6%,44.9%)',
  brown500: 'hsl(19,33.3%,34.1%)',
  brown600: 'hsl(18,33.3%,27.1%)',
  brown700: 'hsl(19,34.1%,17.8%)',
  cobalt50: 'hsl(232,60%,95.1%)',
  cobalt100: 'hsl(230,50%,88.2%)',
  cobalt200: 'hsl(234,58.5%,73.5%)',
  cobalt300: 'hsl(234,56.4%,56.9%)',

  cobalt400: 'hsl(234,86.5%,40.6%)',
  cobalt500: 'hsl(234,87.7%,32%)',
  cobalt600: 'hsl(234,86.7%,23.5%)',
  cobalt700: 'hsl(234,87.8%,16.1%)',
  gray50: 'hsl(0,0%,96.5%)',
  gray100: 'hsl(0,0%,93.3%)',
  gray200: 'hsl(0,0%,88.6%)',
  gray300: 'hsl(0,0%,79.6%)',

  gray400: 'hsl(0,0%,68.6%)',
  gray500: 'hsl(0,0%,45.9%)',
  gray600: 'hsl(0,0%,32.9%)',
  gray700: 'hsl(0,0%,20%)',
  gray800: 'hsl(0,0%,12.2%)',
  gray900: 'hsl(0,0%,7.8%)',
  green50: 'hsl(155,31.6%,92.5%)',
  green100: 'hsl(154,42.6%,77.5%)',
  green200: 'hsl(151,53.8%,61%)',
  green300: 'hsl(151,94%,39%)',
  green400: 'hsl(151,93.5%,30%)',
  green500: 'hsl(151,94.8%,22.5%)',
  green600: 'hsl(151,93.4%,17.8%)',
  green700: 'hsl(152,62.8%,16.9%)',
  orange50: 'hsl(15,100%,96.9%)',
  orange100: 'hsl(16,100%,92%)',
  orange200: 'hsl(17,89.5%,81.4%)',
  orange300: 'hsl(17,93.5%,69.6%)',
  orange400: 'hsl(15,100%,60.8%)',
  orange500: 'hsl(15,65%,45.9%)',
  orange600: 'hsl(15,64.7%,36.7%)',
  orange700: 'hsl(15,64.8%,24.5%)',
  platinum50: 'hsl(189,46.7%,97.1%)',
  platinum100: 'hsl(190,42.9%,94.5%)',
  platinum200: 'hsl(194,32.5%,84.9%)',
  platinum300: 'hsl(199,27.9%,71.2%)',
  platinum400: 'hsl(199,15.9%,61.8%)',
  platinum500: 'hsl(198,9.6%,46.9%)',
  platinum600: 'hsl(199,10.1%,37.1%)',
  platinum700: 'hsl(200,9.5%,24.7%)',
  platinum800: 'hsl(195,33.3%,11.8%)',
  purple50: 'hsl(255,40%,96.1%)',
  purple100: 'hsl(257,44.7%,90.8%)',
  purple200: 'hsl(257,44.2%,79.6%)',
  purple300: 'hsl(257,44.6%,65.3%)',
  purple400: 'hsl(257,45.1%,54.3%)',
  purple500: 'hsl(257,38.1%,41.2%)',
  purple600: 'hsl(256,37.7%,32.7%)',
  purple700: 'hsl(257,38.2%,21.6%)',
  red50: 'hsl(7,100%,96.5%)',
  red100: 'hsl(7,95.7%,91%)',
  red200: 'hsl(7,78%,75.1%)',
  red300: 'hsl(7,77.5%,60%)',
  red400: 'hsl(7,100%,44.1%)',
  red500: 'hsl(7,100%,33.5%)',
  red600: 'hsl(7,100%,26.5%)',
  red700: 'hsl(7,100%,17.6%)',
  white: 'hsl(0,0%,100%)',
  yellow50: 'hsl(40,100%,97.1%)',
  yellow100: 'hsl(39,100%,92.5%)',
  yellow200: 'hsl(40,100%,83.7%)',
  yellow300: 'hsl(40,100%,72%)',
  yellow400: 'hsl(40,100%,63.1%)',
  yellow500: 'hsl(40,62.1%,45.5%)',
  yellow600: 'hsl(40,58.5%,37.8%)',
  yellow700: 'hsl(39,58.5%,25.5%)',
};

export const lightTheme: ColorTheme = {
  background: {
    accent: colors.blue400,
    disabled: colors.gray50,
    negative: colors.red400,
    positive: colors.green400,
    primaryA: colors.white,
    primaryB: colors.black,
    secondary: colors.gray50,
    tertiary: colors.gray100,
    warning: colors.yellow400,
  },
  border: {
    accent: colors.blue400,
    disabled: colors.gray50,
    negative: colors.red200,
    positive: colors.green200,
    primaryA: colors.gray500,
    primaryB: colors.gray700,
    secondary: colors.gray400,
    tertiary: colors.gray300,
    warning: colors.yellow200,
  },
  overlay: {
    dark: 'rgba(0, 0, 0, 0.60)',
    light: 'rgba(0, 0, 0, 0.38)',
  },
  statusBar: 'dark-content',
  tag: {
    blue: colors.blue50,
    brown: colors.brown50,
    cobalt: colors.cobalt50,
    gray: colors.gray50,
    green: colors.green50,
    orange: colors.orange50,
    platinum: colors.platinum50,
    purple: colors.purple50,
    red: colors.red50,
    yellow: colors.yellow50,
  },
  text: {
    accent: colors.blue400,
    disabled: colors.gray400,
    negative: colors.red400,
    positive: colors.green400,
    primaryA: colors.black,
    primaryB: colors.white,
    secondary: colors.gray600,
    tertiary: colors.gray500,
    warning: colors.yellow400,
  },
};

export const darkTheme: ColorTheme = {
  background: {
    accent: colors.blue400,
    disabled: colors.gray800,
    negative: colors.red500,
    positive: colors.green400,
    primaryA: colors.gray900,
    primaryB: colors.white,
    secondary: colors.gray800,
    tertiary: colors.gray700,
    warning: colors.yellow500,
  },
  border: {
    accent: colors.blue400,
    disabled: colors.gray800,
    negative: colors.red500,
    positive: colors.green500,
    primaryA: colors.gray300,
    primaryB: colors.gray100,
    secondary: colors.gray500,
    tertiary: colors.gray600,
    warning: colors.yellow500,
  },
  overlay: {
    dark: 'rgba(0, 0, 0, 0.60)',
    light: 'rgba(0, 0, 0, 0.38)',
  },
  statusBar: 'light-content',
  tag: {
    blue: colors.blue700,
    brown: colors.brown700,
    cobalt: colors.cobalt700,
    gray: colors.gray700,
    green: colors.green700,
    orange: colors.orange700,
    platinum: colors.platinum700,
    purple: colors.purple700,
    red: colors.red700,
    yellow: colors.yellow700,
  },
  text: {
    accent: colors.blue400,
    disabled: colors.gray600,
    negative: colors.red500,
    positive: colors.green400,
    primaryA: colors.gray200,
    primaryB: colors.gray900,
    secondary: colors.gray400,
    tertiary: colors.gray500,
    warning: colors.yellow500,
  },
};
